import Swal from "sweetalert2";
import { __CheckFictitiousNameSimilarity } from "request/fictitiousProviders/__CheckFictitiousNameSimilarity";
import { __CheckFictitiousFormSimilarity } from "request/fictitiousProviders/__CheckFictitiousFormSimilarity";

// Caché en memoria por nombre: la alerta y el icono de advertencia consultan lo
// mismo, así el segundo no vuelve a golpear el servidor.
const matchCache = new Map();

/**
 * Consulta la mejor coincidencia del nombre con la lista de proveedores
 * ficticios. Retorna el match {similarity, name, nit, resolution_number,
 * resolution_date} o null si no hay coincidencia (incluidos los usuarios de rol
 * DIAN, a quienes el backend nunca les entrega esta información).
 */
export const fetchFictitiousNameMatch = async (token, name) => {
  const plainName = String(name ?? "").trim().toUpperCase();
  if (!plainName) return null;

  // El token entra en la clave para que lo consultado por un usuario no se
  // reutilice si en la misma sesión del navegador entra otro (un usuario DIAN
  // jamás debe ver una coincidencia consultada por alguien más).
  const key = `${token ?? ""}::${plainName}`;

  if (matchCache.has(key)) return matchCache.get(key);

  const res = await __CheckFictitiousNameSimilarity(token, String(name).trim());
  const match = res?.data?.status?.code === 200 ? res?.data?.status?.data ?? null : null;

  matchCache.set(key, match);
  return match;
};

const escapeHtml = (text = "") =>
  String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * Caso 1032 — Alerta INFORMATIVA (no bloqueante) de similitud de nombre con la
 * lista de proveedores ficticios. Consulta el backend y, si hay una coincidencia
 * que alcanza el umbral, muestra un SweetAlert con dos opciones.
 *
 * @param {Object} params
 * @param {string} params.token JWT del usuario.
 * @param {string} params.name  Nombre a verificar (proveedor o importador).
 * @param {string} params.fieldLabel Etiqueta del campo donde se ingresó el nombre,
 * tal como se ve en pantalla ("Proveedor/Exportador", "Importador/Comprador"...).
 * @param {Function} [params.isStillRelevant] Se evalúa tras la consulta y antes de
 * abrir la alerta: si retorna false (el usuario ya cambió o limpió la selección)
 * la alerta se descarta, para no mostrar un aviso que no corresponde a lo visible.
 * @returns {Promise<boolean>} true si se puede continuar (sin coincidencia, error
 * de red o el usuario eligió continuar); false si el usuario canceló la operación.
 */
export const checkFictitiousNameSimilarity = async ({
  token,
  name,
  fieldLabel = "Proveedor/Exportador",
  isStillRelevant = null
}) => {
  try {
    if (!name || !String(name).trim()) return true;

    const match = await fetchFictitiousNameMatch(token, name);
    if (!match) return true;

    if (isStillRelevant && !isStillRelevant()) return true;

    const result = await Swal.fire({
      title: "Advertencia: Posible Coincidencia Encontrada",
      html: `<p style="text-align: left;">El nombre (<b>"${escapeHtml(name)}"</b>) ingresado en el campo <b>${escapeHtml(fieldLabel)}</b>, presenta una similitud del <b>${match.similarity}%</b> con el proveedor <b>"${escapeHtml(match.name)}"</b> (NIT ${escapeHtml(match.nit)}), registrado por DIAN como proveedor ficticio. Resolución ${escapeHtml(match.resolution_number)} de ${match.resolution_date}.</p>
        <p style="text-align: left;"><b>Importante:</b> Esta alerta tiene carácter informativo. Una coincidencia en el nombre no significa necesariamente que corresponda al mismo proveedor. Antes de continuar, verifique el <b>NIT</b>, y la <b>razón social</b> del tercero.</p>
        <p style="text-align: left;"><b>Continuar de todas formas:</b> conserva lo seleccionado en ${escapeHtml(fieldLabel)}.<br/>
        <b>Quitar y elegir otro:</b> deja el campo vacío para que seleccione otro tercero.</p>`,
      icon: "warning",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      cancelButtonText: "Quitar y elegir otro",
      confirmButtonColor: "#1F7AC3",
      confirmButtonText: "Continuar de todas formas",
      customClass: { title: "class-text" }
    });

    // Solo el botón "Quitar y elegir otro" limpia la selección: cerrar con Esc,
    // clic fuera del modal o el reemplazo por otra alerta cuentan como continuar.
    const keepSelection =
      result.isConfirmed || result.dismiss !== Swal.DismissReason.cancel;

    if (!keepSelection) {
      // El campo queda vacío a propósito; se avisa para que no parezca que el
      // formulario perdió el dato por su cuenta y el usuario sepa que debe elegir otro.
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "info",
        title: `Se quitó la selección de ${fieldLabel}. Elija otro para poder guardar.`,
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true
      });
    }

    return keepSelection;
  } catch (error) {
    // La alerta es informativa: un fallo del chequeo nunca debe romper el flujo.
    return true;
  }
};

/**
 * Caso 1032 — Advertencia antes de un CAMBIO DE ESTADO (presentar, aprobar,
 * autorizar, revisar...). A diferencia de la alerta al seleccionar el tercero,
 * aquí "Cancelar operación" no borra nada: simplemente no se ejecuta el cambio
 * de estado, que es la operación en curso.
 *
 * Se consulta al backend por el id del formulario, así funciona igual desde la
 * vista, la revisión o el listado, sin depender de lo que tenga cargado la
 * pantalla. Los usuarios de rol DIAN nunca reciben coincidencias.
 *
 * @returns {Promise<boolean>} true si se debe continuar con el cambio de estado.
 */
export const confirmStatusChangeAgainstFictitious = async ({
  token,
  formType,
  formId,
  statusLabel = ""
}) => {
  try {
    if (!formType || !formId) return true;

    const res = await __CheckFictitiousFormSimilarity(token, formType, formId);
    const matches =
      res?.data?.status?.code === 200 ? res?.data?.status?.data?.matches ?? [] : [];

    if (!matches.length) return true;

    const detalle = matches
      .map(
        (m) =>
          `<li style="text-align: left;"><b>${escapeHtml(m.field)}</b>: "${escapeHtml(m.value)}" coincide en <b>${m.similarity}%</b> con <b>"${escapeHtml(m.name)}"</b> (NIT ${escapeHtml(m.nit)}), Resolución ${escapeHtml(m.resolution_number)} de ${m.resolution_date}.</li>`
      )
      .join("");

    const accion = statusLabel ? ` a <b>${escapeHtml(statusLabel)}</b>` : "";

    const result = await Swal.fire({
      title: "Advertencia: Posible Coincidencia Encontrada",
      html: `<p style="text-align: left;">Este formulario tiene datos que coinciden con la lista de proveedores ficticios de la DIAN:</p>
        <ul>${detalle}</ul>
        <p style="text-align: left;"><b>Importante:</b> Esta alerta tiene carácter informativo. Una coincidencia en el nombre no significa necesariamente que corresponda al mismo proveedor. Antes de continuar, verifique el <b>NIT</b> y la <b>razón social</b> del tercero.</p>
        <p style="text-align: left;"><b>Continuar de todas formas:</b> realiza el cambio de estado${accion}.<br/>
        <b>Cancelar operación:</b> no cambia el estado del formulario y no modifica ningún dato.</p>`,
      icon: "warning",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar operación",
      confirmButtonColor: "#1F7AC3",
      confirmButtonText: "Continuar de todas formas",
      customClass: { title: "class-text" }
    });

    // Cerrar con Esc o clic fuera se toma como cancelar: en un cambio de estado
    // lo prudente es no ejecutarlo si el usuario no confirmó expresamente.
    return result.isConfirmed === true;
  } catch (error) {
    // Es informativa: si el chequeo falla no se bloquea el cambio de estado.
    return true;
  }
};
